import { createContext, useContext } from 'react';
import { AdminPaymentStore, adminPaymentStore } from './admin_payment_store';
import { AuthStore, authStore } from './auth_store';
import { RequestStore, requestStore } from './request_store';
import { WorkStore, workStore } from './work_store';
import { MentorStore, mentorStore } from './mentor_store';
import { ManagerQueueStore, managerQueueStore } from './manager_queue_store';
import { CallStore, callStore } from './call_store';
import { EmailStore, emailStore } from './email_store';
import { FileStore, fileStore } from './file_store';
import { AttachmentStore, attachmentStore } from './attachment_store';
import { UserMetadataStore } from './user_metadata_store';
import { SettingsStore, settingsStore } from './settings_store';
import { ShiftHistoryStore, shiftHistoryStore } from './shift_history_store';
import {
  SubscriptionPaymentStore,
  subscriptionPaymentStore,
} from './subscription_payment_store';
import { DwollaStore, dwollaStore } from './dwolla_store';
import { FieldguideStore, fieldguideStore } from './fieldguide_store';
import { ForumStore, forumStore } from './forum_store';
import { CalendarAuthStore, calendarAuthStore } from './calendar_auth_store';
import { CalendarEventStore, calendarEventStore } from './calendar_event_store';
import { RecurringStore, recurringStore } from './recurring_store';
import { ThemeStore } from './theme_store';
import type { FHRequest, FHRequestMessage, ShiftHistoryItem } from '../types';

// Create singleton instance for theme
const themeStore = new ThemeStore();

// Create singleton instance for user metadata
const userMetadataStore = new UserMetadataStore();

/**
 * Root store that aggregates all domain stores
 * Add new stores here as the application grows
 */
export class RootStore {
  adminPayments: AdminPaymentStore;
  auth: AuthStore;
  requests: RequestStore;
  work: WorkStore;
  mentors: MentorStore;
  managerQueue: ManagerQueueStore;
  calls: CallStore;
  emails: EmailStore;
  fieldguide: FieldguideStore;
  files: FileStore;
  attachments: AttachmentStore;
  userMetadata: UserMetadataStore;
  settings: SettingsStore;
  shiftHistory: ShiftHistoryStore;
  subscriptionPayments: SubscriptionPaymentStore;
  dwolla: DwollaStore;
  forum: ForumStore;
  calendarAuth: CalendarAuthStore;
  calendarEvents: CalendarEventStore;
  recurring: RecurringStore;
  theme: ThemeStore;

  constructor() {
    this.adminPayments = adminPaymentStore;
    this.auth = authStore;
    this.requests = requestStore;
    this.work = workStore;
    this.mentors = mentorStore;
    this.managerQueue = managerQueueStore;
    this.calls = callStore;
    this.emails = emailStore;
    this.fieldguide = fieldguideStore;
    this.files = fileStore;
    this.attachments = attachmentStore;
    this.userMetadata = userMetadataStore;
    this.settings = settingsStore;
    this.shiftHistory = shiftHistoryStore;
    this.subscriptionPayments = subscriptionPaymentStore;
    this.dwolla = dwollaStore;
    this.forum = forumStore;
    this.calendarAuth = calendarAuthStore;
    this.calendarEvents = calendarEventStore;
    this.recurring = recurringStore;
    this.theme = themeStore;
  }

  /**
   * Initialize all stores that need async initialization
   */
  async initialize(): Promise<void> {
    await this.auth.initialize();
  }

  /**
   * Handle realtime updates from Socket.io
   * Routes updates to appropriate stores based on payload type
   */
  handleRealtimeUpdate(type: string, payload: unknown): void {
    switch (type) {
      case 'request':
        this.requests.receiveRequestUpdate(payload as FHRequest);
        break;
      case 'request_message': {
        // Message payload should include the request UUID
        const message = payload as FHRequestMessage & { request_uuid?: string };
        if (message.request_uuid) {
          this.requests.receiveMessageUpdate(message.request_uuid, message);
        }
        break;
      }
      case 'shift_history':
        this.shiftHistory.receiveUpdate(payload as ShiftHistoryItem);
        break;
      default:
        console.log('Unhandled realtime update:', type, payload);
    }
  }
}

// Singleton instance
export const rootStore = new RootStore();

// React context for stores
export const StoreContext = createContext<RootStore>(rootStore);

// Hook to access stores
export function useStore(): RootStore {
  return useContext(StoreContext);
}

// Convenience hooks for individual stores
export function useAuthStore(): AuthStore {
  return useStore().auth;
}

export function useRequestStore(): RequestStore {
  return useStore().requests;
}

export function useWorkStore(): WorkStore {
  return useStore().work;
}

export function useMentorStore(): MentorStore {
  return useStore().mentors;
}

export function useCallStore(): CallStore {
  return useStore().calls;
}

export function useEmailStore(): EmailStore {
  return useStore().emails;
}

export function useFileStore(): FileStore {
  return useStore().files;
}

export function useAttachmentStore(): AttachmentStore {
  return useStore().attachments;
}

export function useUserMetadataStore(): UserMetadataStore {
  return useStore().userMetadata;
}

export function useSettingsStore(): SettingsStore {
  return useStore().settings;
}

export function useAdminPaymentStore(): AdminPaymentStore {
  return useStore().adminPayments;
}

export function useShiftHistoryStore(): ShiftHistoryStore {
  return useStore().shiftHistory;
}

export function useSubscriptionPaymentStore(): SubscriptionPaymentStore {
  return useStore().subscriptionPayments;
}

export function useManagerQueueStore(): ManagerQueueStore {
  return useStore().managerQueue;
}

export function useDwollaStore(): DwollaStore {
  return useStore().dwolla;
}

export function useFieldguideStore(): FieldguideStore {
  return useStore().fieldguide;
}

export function useForumStore(): ForumStore {
  return useStore().forum;
}

export function useCalendarAuthStore(): CalendarAuthStore {
  return useStore().calendarAuth;
}

export function useCalendarEventStore(): CalendarEventStore {
  return useStore().calendarEvents;
}

export function useRecurringStore(): RecurringStore {
  return useStore().recurring;
}

export function useThemeStore(): ThemeStore {
  return useStore().theme;
}
